import React from 'react';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import styled from 'styled-components';

import { CalendarDirection } from '@rover/components/src/datetime/DatePicker';
import ServiceTypeFilter, {
  ServiceFilterType,
} from '@rover/components/src/formFields/ServiceTypeFilter';
import { LOCATION_DEFAULT_TITLE_1 } from '@rover/constants/src/location/location.constants';
import {
  DROP_OFF_PICK_UP_SUPPORTED_SERVICES,
  EMPTY_LOCATION,
} from '@rover/constants/src/search/searchPage.constants';
import { Alignment, HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import { AdvancedFiltersButton } from '@rover/react-lib/src/pages/search/SearchPage/components/AdvancedFiltersButton';
import {
  DayTimeAvailability,
  DaytimeServiceScheduler,
  FulltimeAvailability,
  OvernightDateRange,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import Location from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Location';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType, ServiceTypeOption } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { CollectionWrapper, FilterWrapper } from '../common';

const BackgroundWrapper = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
`;
const StyledFilterWrapper = styled(FilterWrapper)`
  margin-bottom: ${({ theme }) => theme.space['3x']};
`;

const TopRowLeft = styled.div`
  width: 55%;
`;

const TopRowRight = styled.div`
  width: 40%;
`;

export type Props = {
  advancedFiltersCount: number;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  isCatOnly: boolean;
  language: string;
  priceFilterMaxValue: number;
  priceFilterMinValue: number;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  serviceTypeOptions: ServiceTypeOption[];
  showAdvancedFiltersModal: () => void;
  updateFiltersAndFireSearchOnChange: (searchFilters: Partial<SearchFilters>) => void;
};

const CondensedFiltersCollection = ({
  advancedFiltersCount,
  getServiceTypeBySlug,
  isCatOnly,
  language,
  priceFilterMaxValue,
  priceFilterMinValue,
  searchFilters,
  serviceTypeFilterOptions,
  serviceTypeOptions,
  showAdvancedFiltersModal,
  updateFiltersAndFireSearchOnChange,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);

  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  // Abstract this into a selector in https://roverdotcom.atlassian.net/browse/DEV-42261
  const startPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Drop off`
    : t`Start`;
  const endPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Pick up`
    : t`End`;

  const handleChange = (filters: Partial<SearchFilters>): void => {
    updateFiltersAndFireSearchOnChange(filters);
  };

  const handleChangeDateRange = (dateRange): void => {
    if (dateRange) {
      handleChange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  };

  const {
    showDateRange,
    showDaytimeAvailability,
    showFulltimeAvailability,
    showDaytimeServiceScheduler,
  } = shouldShowControls({
    serviceOptions: serviceTypeFilterOptions,
    isCatOnly: isCatOnly || isCatOnlyBrand,
    serviceType: serviceType || undefined,
    serviceTypeSlug: searchFilters.serviceType,
  });

  const maxDate = getMaxRequestDate();

  return (
    <BackgroundWrapper data-qa-id="search-condensed-filters-wrapper">
      <CollectionWrapper>
        <StyledFilterWrapper>
          <HorizontalLayout hAlign={Alignment.SPACE_BETWEEN} vAlign={Alignment.END}>
            <TopRowLeft>
              <ServiceTypeFilter
                filterType={ServiceFilterType.DROPDOWN}
                serviceTypeOptions={serviceTypeOptions}
                label={t`Show me sitters for`}
                searchFilters={searchFilters}
                onChange={handleChange}
                priceFilterMaxValue={priceFilterMaxValue}
                priceFilterMinValue={priceFilterMinValue}
              />
            </TopRowLeft>
            <TopRowRight>
              <Location
                locationInputId="location-input-condensed"
                label={i18n._(LOCATION_DEFAULT_TITLE_1)}
                location={searchFilters.location}
                onChange={handleChange}
                onClear={() => handleChange(EMPTY_LOCATION)}
                suggestionsMenuAlignment="right"
              />
            </TopRowRight>
          </HorizontalLayout>
        </StyledFilterWrapper>
        {showDateRange && (
          <StyledFilterWrapper>
            <OvernightDateRange
              calendarDirection={CalendarDirection.DOWN}
              endPlaceholder={endPlaceholder}
              maxDate={maxDate}
              label=""
              language={language}
              onChange={handleChangeDateRange}
              searchFilters={searchFilters}
              startPlaceholder={startPlaceholder}
            />
          </StyledFilterWrapper>
        )}
        {showDaytimeServiceScheduler && (
          <StyledFilterWrapper>
            <DaytimeServiceScheduler
              oneTimeLabel=""
              repeatStartDateLabel=""
              calendarDirection={CalendarDirection.DOWN}
              onChange={(frequency) => handleChange({ frequency })}
              endPlaceholder={endPlaceholder}
              language={language}
              maxDate={maxDate}
              onDateRangeChange={handleChangeDateRange}
              onSelectedDaysChange={(selectedDays) => handleChange({ selectedDays })}
              onStartDateChange={handleChangeDateRange}
              searchFilters={searchFilters}
              serviceType={serviceType}
              showLabel={false}
              startPlaceholder={startPlaceholder}
              showArrow
            />
          </StyledFilterWrapper>
        )}
        {showFulltimeAvailability && (
          <StyledFilterWrapper>
            <FulltimeAvailability
              label={t`Sitter is home full-time`}
              searchFilters={searchFilters}
              onChange={(fulltimeAvailability) => handleChange({ fulltimeAvailability })}
            />
          </StyledFilterWrapper>
        )}
        {showDaytimeAvailability && (
          <StyledFilterWrapper>
            <DayTimeAvailability
              availabilityBuckets={{
                morning: morningAvailability ? morningAvailability.label : null,
                midday: middayAvailability ? middayAvailability.label : null,
                evening: eveningAvailability ? eveningAvailability.label : null,
              }}
              label={t`Which time(s) do you need?`}
              searchFilters={searchFilters}
              onChange={(availability) => handleChange(availability)}
            />
          </StyledFilterWrapper>
        )}
        <HorizontalLayout hAlign={Alignment.CENTER}>
          <AdvancedFiltersButton
            selectedFilterCount={advancedFiltersCount}
            onClick={showAdvancedFiltersModal}
          />
        </HorizontalLayout>
      </CollectionWrapper>
    </BackgroundWrapper>
  );
};

export default CondensedFiltersCollection;
